// src/pages/LandingPage.js
// HomeIQa — Main landing page
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import Logo from '../components/Logo';

export default function LandingPage() {
  const navigate = useNavigate();
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setSignedIn(!!u));
    return unsub;
  }, []);

  return (
    <div style={s.page}>

      {/* NAV */}
      <nav style={s.nav}>
        <Logo dark />
        <div style={s.navLinks}>
          <a href="#how" style={s.navLink}>How it works</a>
          <a href="#includes" style={s.navLink}>What's included</a>
          <a href="/blog" style={s.navLink}>Blog</a>
          <a href="/appraisers" style={s.navLink}>Find an appraiser</a>
        </div>
        <button style={s.navCta} onClick={() => navigate('/start')}>
          Get my report — $29
        </button>
      </nav>

      {/* HERO */}
      <section style={s.hero}>
        <div style={s.heroLeft}>
          <div style={s.heroBadge}>Home Intelligence Analysis</div>
          <h1 style={s.heroH1}>
            Know exactly what your home is <em style={s.heroEm}>worth</em> before you decide.
          </h1>
          <p style={s.heroSub}>
            A 12-page AI-generated report grounded in real government property records,
            live market comps, and verified sale history. Not an estimate. An analysis.
          </p>
          <div style={s.heroActions}>
            <button style={s.heroPrimary} onClick={() => navigate('/start')}>
              Start my valuation — $29
            </button>
            <a href="/appraisers" style={s.heroGhost}>Need an official appraisal? →</a>
          </div>
        </div>

        {/* FLOATING REPORT CARD */}
        <div style={s.heroCard}>
          <div style={s.cardTop}>
            <div style={s.cardEyebrow}>HomeIQa · Confidential Report</div>
            <div style={s.cardAddress}>2847 Thornwood Lane, Atlanta GA</div>
            <div style={s.cardDate}>Verified against Fulton County records</div>
          </div>
          <div style={s.cardBody}>
            <div style={s.cardValueLabel}>Estimated market value</div>
            <div style={s.cardValue}>$418,000</div>
            <div style={s.cardRange}>Range: $404,000 – $434,000 · 84% confidence</div>
            <div style={s.cardMetrics}>
              {[
                { label: 'Equity gained', val: '+$121K' },
                { label: 'Best to sell', val: 'Apr–Jun' },
                { label: 'Net proceeds', val: '$388K' }
              ].map(m => (
                <div key={m.label} style={s.cardMetric}>
                  <span style={s.cardMetricVal}>{m.val}</span>
                  <span style={s.cardMetricLabel}>{m.label}</span>
                </div>
              ))}
            </div>
            <div style={s.cardRec}>
              <strong>Top rec:</strong> Kitchen refresh ($4,200) → projected +$13,500 return
            </div>
            <div style={s.cardBlur} />
            <div style={s.cardUnlock}>
              <div>
                <div style={s.cardPrice}>$29</div>
                <div style={s.cardPriceSub}>Instant PDF download</div>
              </div>
              <button style={s.cardUnlockBtn} onClick={() => navigate('/start')}>
                Get full report
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <div style={s.trustBar}>
        {[
          'Verified against government records',
          'Real-time comps from Zillow & Redfin',
          'Delivered in under 2 minutes',
          'No agents · No upsells · No follow-up calls'
        ].map(item => (
          <div key={item} style={s.trustItem}>
            <span style={s.trustDot} />
            {item}
          </div>
        ))}
      </div>

      {/* HOW IT WORKS */}
      <section id="how" style={s.section}>
        <div style={s.sectionTag}>The process</div>
        <h2 style={s.sectionH2}>Three steps to a report you can <em style={s.em}>actually trust</em></h2>
        <div style={s.stepsGrid}>
          {[
            { num: '01', title: 'Tell us about your home', desc: 'Answer 10 questions — address, when you bought it, what you paid, square footage, condition, and your goals. Takes 3 minutes.' },
            { num: '02', title: 'We verify against real records', desc: 'Our system cross-checks your purchase details against county property records, then pulls live comps from Zillow, Redfin, and local MLS data.' },
            { num: '03', title: 'Your report arrives instantly', desc: 'AI generates a 12-page personalized PDF — value range, equity analysis, best time to sell, improvement ROI, and net proceeds estimate.' }
          ].map(step => (
            <div key={step.num} style={s.stepCard}>
              <div style={s.stepAccent} />
              <div style={s.stepNum}>{step.num}</div>
              <div style={s.stepTitle}>{step.title}</div>
              <p style={s.stepDesc}>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section id="includes" style={{ ...s.section, background: '#FAF7F2' }}>
        <div style={s.sectionTag}>What's included</div>
        <h2 style={s.sectionH2}>Everything a seller needs to make a <em style={s.em}>confident</em> decision</h2>
        <div style={s.includesList}>
          {[
            { n: '1', title: 'Verified value range', desc: 'Cross-referenced against county tax records and 5+ recent comparable sales within 0.5 miles.' },
            { n: '2', title: 'Equity & net proceeds breakdown', desc: 'Exact figures on what you\'d walk away with after agent fees, closing costs, and outstanding mortgage.' },
            { n: '3', title: 'Optimal selling window', desc: 'Month-by-month analysis of when homes like yours sell fastest and for the most in your ZIP code.' },
            { n: '4', title: 'Top 3 improvement ROI picks', desc: 'Specific upgrades ranked by cost vs. projected return — actual dollar estimates, not general advice.' },
            { n: '5', title: 'Neighborhood market health score', desc: 'Days on market, list-to-sale ratio, inventory levels, and price trend for your exact area.' }
          ].map(item => (
            <div key={item.n} style={s.includeItem}>
              <span style={s.includeNum}>{item.n}</span>
              <div>
                <div style={s.includeTitle}>{item.title}</div>
                <div style={s.includeDesc}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING CTA */}
      <section style={s.pricingSection}>
        <div style={s.pricingTag}>Simple pricing</div>
        <h2 style={s.pricingH2}>One report. One price. No <em style={s.heroEm}>surprises.</em></h2>
        <div style={s.pricingCard}>
          <div style={s.pricingPrice}><sup style={s.pricingSup}>$</sup>29</div>
          <div style={s.pricingSub}>One-time · Instant PDF download · No subscription</div>
          <ul style={s.pricingFeatures}>
            {[
              '12-page personalized valuation report',
              'Verified against county property records',
              'Live market comps (Zillow, Redfin, MLS)',
              'Equity & net proceeds breakdown',
              'Top 3 improvement ROI recommendations',
              'Optimal selling window analysis',
              'Neighborhood market health score',
              'Delivered in under 2 minutes'
            ].map(f => (
              <li key={f} style={s.pricingFeature}>
                <span style={s.checkmark}>✓</span> {f}
              </li>
            ))}
          </ul>
          <button style={s.pricingCta} onClick={() => navigate('/start')}>
            Get my HomeIQa report now
          </button>
          <div style={s.pricingNote}>Secure payment via PayPal · PDF delivered instantly to your email</div>
        </div>
      </section>

      {/* Floating Agent Login / Dashboard link — bottom-right */}
      <a
        href={signedIn ? '/agent/dashboard' : '/agent/login'}
        style={signedIn ? { ...s.agentLogin, ...s.agentLoginActive } : s.agentLogin}
      >
        <span style={signedIn ? { ...s.agentLoginDot, ...s.agentLoginDotActive } : s.agentLoginDot} />
        {signedIn ? 'Dashboard' : 'Agent Login'}
      </a>

      {/* Footer is rendered globally from App.js */}
    </div>
  );
}

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const GOLD_PALE = '#F7F0DC';
const CREAM = '#FAF7F2';
const MID = '#5A5A5A';
const LIGHT = '#9A9A9A';
const BORDER = 'rgba(107,30,46,0.1)';
const WHITE = '#FFFFFF';

const s = {
  page: { fontFamily: "'DM Sans', sans-serif", background: WHITE, color: '#1C1C1C' },
  nav: { position: 'sticky', top: 0, zIndex: 100, background: WHITE, borderBottom: `1px solid ${BORDER}`, padding: '0 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 68 },
  navLinks: { display: 'flex', gap: 36 },
  navLink: { fontSize: 13, color: MID, textDecoration: 'none' },
  navCta: { background: BURG, color: WHITE, border: 'none', borderRadius: 4, padding: '10px 22px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  hero: { background: BURG_DK, padding: '80px 64px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'center', position: 'relative', overflow: 'hidden' },
  heroBadge: { display: 'inline-block', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: 2, padding: '5px 14px', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#E2C47A', marginBottom: 20 },
  heroH1: { fontFamily: "'Cormorant Garamond', serif", fontSize: 46, fontWeight: 500, lineHeight: 1.15, color: WHITE, letterSpacing: '-0.5px', marginBottom: 18 },
  heroEm: { fontStyle: 'italic', color: '#E2C47A' },
  heroSub: { fontSize: 15, fontWeight: 300, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: 32, maxWidth: 420 },
  heroActions: { display: 'flex', alignItems: 'center', gap: 20 },
  heroPrimary: { background: GOLD, color: BURG_DK, border: 'none', borderRadius: 4, padding: '14px 28px', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  heroGhost: { fontSize: 13, color: 'rgba(255,255,255,0.6)', textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.2)' },
  heroCard: { background: WHITE, borderRadius: 8, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.4)', animation: 'float 5s ease-in-out infinite' },
  cardTop: { background: BURG, padding: '18px 22px' },
  cardEyebrow: { fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 5 },
  cardAddress: { fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 500, color: WHITE, marginBottom: 2 },
  cardDate: { fontSize: 11, color: 'rgba(255,255,255,0.4)' },
  cardBody: { padding: '20px 22px' },
  cardValueLabel: { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: LIGHT, marginBottom: 4 },
  cardValue: { fontFamily: "'Cormorant Garamond', serif", fontSize: 40, fontWeight: 600, color: BURG, lineHeight: 1, marginBottom: 4 },
  cardRange: { fontSize: 11, color: LIGHT, marginBottom: 14 },
  cardMetrics: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 },
  cardMetric: { background: CREAM, padding: '10px', borderRadius: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 3 },
  cardMetricVal: { fontFamily: "'Cormorant Garamond', serif", fontSize: 16, fontWeight: 600, color: BURG },
  cardMetricLabel: { fontSize: 9, color: LIGHT, textTransform: 'uppercase', letterSpacing: '0.05em' },
  cardRec: { fontSize: 11, color: MID, padding: '10px 12px', background: GOLD_PALE, borderLeft: `3px solid ${GOLD}`, borderRadius: '0 4px 4px 0', marginBottom: 12, lineHeight: 1.5 },
  cardBlur: { height: 28, background: 'linear-gradient(to right, #f0e8e8, #ede8e0)', borderRadius: 4, opacity: 0.4, marginBottom: 14, filter: 'blur(3px)' },
  cardUnlock: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 14, borderTop: `1px solid ${BORDER}` },
  cardPrice: { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 600, color: BURG },
  cardPriceSub: { fontSize: 10, color: LIGHT },
  cardUnlockBtn: { background: BURG, color: WHITE, border: 'none', borderRadius: 4, padding: '9px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  trustBar: { background: GOLD_PALE, borderBottom: `1px solid rgba(201,168,76,0.25)`, padding: '14px 48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 40, flexWrap: 'wrap' },
  trustItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: BURG_DK },
  trustDot: { width: 6, height: 6, borderRadius: '50%', background: '#1D9E75', flexShrink: 0 },
  section: { padding: '80px 64px', background: WHITE },
  sectionTag: { fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: GOLD, borderBottom: `1px solid ${GOLD}`, paddingBottom: 4, display: 'inline-block', marginBottom: 18 },
  sectionH2: { fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 500, color: BURG_DK, lineHeight: 1.2, letterSpacing: '-0.3px', maxWidth: 520, marginBottom: 44 },
  em: { fontStyle: 'italic', color: BURG },
  stepsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, border: `1px solid ${BORDER}`, borderRadius: 6, overflow: 'hidden' },
  stepCard: { padding: '36px', borderRight: `1px solid ${BORDER}`, background: WHITE, position: 'relative' },
  stepAccent: { position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: GOLD },
  stepNum: { fontFamily: "'Cormorant Garamond', serif", fontSize: 44, fontWeight: 400, color: GOLD_PALE, lineHeight: 1, marginBottom: 16 },
  stepTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 500, color: BURG, marginBottom: 10 },
  stepDesc: { fontSize: 13, fontWeight: 300, color: MID, lineHeight: 1.7 },
  includesList: { maxWidth: 640 },
  includeItem: { display: 'flex', alignItems: 'flex-start', gap: 16, padding: '18px 0', borderBottom: `1px solid ${BORDER}` },
  includeNum: { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 400, color: GOLD, lineHeight: 1, minWidth: 24, marginTop: -2 },
  includeTitle: { fontSize: 15, fontWeight: 500, color: '#1C1C1C', marginBottom: 4 },
  includeDesc: { fontSize: 13, fontWeight: 300, color: MID, lineHeight: 1.65 },
  pricingSection: { background: BURG_DK, padding: '80px 48px', textAlign: 'center' },
  pricingTag: { fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#E2C47A', borderBottom: '1px solid rgba(201,168,76,0.4)', paddingBottom: 4, display: 'inline-block', marginBottom: 18 },
  pricingH2: { fontFamily: "'Cormorant Garamond', serif", fontSize: 40, fontWeight: 500, color: WHITE, letterSpacing: '-0.5px', maxWidth: 520, margin: '0 auto 40px', lineHeight: 1.18 },
  pricingCard: { display: 'inline-block', background: WHITE, borderRadius: 8, padding: '44px 52px', textAlign: 'left', maxWidth: 440, width: '100%' },
  pricingPrice: { fontFamily: "'Cormorant Garamond', serif", fontSize: 60, fontWeight: 600, color: BURG, lineHeight: 1, marginBottom: 4 },
  pricingSup: { fontSize: 28, verticalAlign: 'super' },
  pricingSub: { fontSize: 12, color: LIGHT, marginBottom: 24 },
  pricingFeatures: { listStyle: 'none', marginBottom: 28 },
  pricingFeature: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: `1px solid ${BORDER}`, fontSize: 14, fontWeight: 300, color: '#1C1C1C' },
  checkmark: { width: 18, height: 18, borderRadius: '50%', background: GOLD_PALE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: BURG, fontWeight: 700, flexShrink: 0 },
  pricingCta: { width: '100%', background: BURG, color: WHITE, border: 'none', borderRadius: 4, padding: '15px', fontSize: 14, fontWeight: 500, cursor: 'pointer', marginBottom: 10, fontFamily: "'DM Sans', sans-serif" },
  pricingNote: { textAlign: 'center', fontSize: 11, color: LIGHT },
  footer: { background: '#1C1C1C', padding: '28px 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 },
  footerLinks: { display: 'flex', gap: 28 },
  footerLink: { fontSize: 12, color: 'rgba(255,255,255,0.35)', textDecoration: 'none' },
  footerCopy: { fontSize: 11, color: 'rgba(255,255,255,0.2)', maxWidth: 480, lineHeight: 1.5 },
  agentLogin: {
    position: 'fixed', bottom: 22, right: 22, zIndex: 90,
    background: BURG_DK, color: '#FFFFFF',
    padding: '10px 18px', borderRadius: 999,
    fontSize: 12, fontWeight: 500, letterSpacing: '0.02em',
    fontFamily: "'DM Sans', sans-serif",
    textDecoration: 'none',
    border: `1px solid ${GOLD}`,
    boxShadow: '0 8px 24px rgba(74,18,32,0.35)',
    display: 'inline-flex', alignItems: 'center', gap: 8,
    transition: 'transform 0.15s ease, box-shadow 0.15s ease'
  },
  agentLoginDot: {
    width: 7, height: 7, borderRadius: '50%', background: GOLD,
    boxShadow: '0 0 0 3px rgba(201,168,76,0.25)'
  },
  agentLoginActive: {
    background: GOLD,
    color: BURG_DK,
    fontWeight: 600,
    letterSpacing: '0.04em',
    border: `1px solid ${BURG_DK}`,
    boxShadow: '0 8px 24px rgba(201,168,76,0.45)'
  },
  agentLoginDotActive: {
    background: BURG_DK,
    boxShadow: '0 0 0 3px rgba(74,18,32,0.25)'
  }
};
